/**
 * Agent Brain event contract v2 — the reader's side.
 *
 * The canonical emitter lives in harmoniq-services-common
 * (``backend/utils/brain_emitter.py``); every service copy reproduces
 * ``src/test/fixtures/brain_contract_v2.json`` byte for byte. An event
 * with ``schema_version: 2`` may carry:
 *
 *   agent    mother | discovery | planner | executor | reviewer |
 *            analyst | writer | checker — the sub-agent inside origin
 *   phase    { id, name, service } — the group it belongs to
 *   code     { lang: sql|python, dialect?, text, chars, truncated,
 *              redactions } or { lang, text, withheld: true }
 *   note     plain text, the "Details" body that is not code
 *   verdict  { outcome: approved|rejected|unavailable, reason?,
 *              dimensions?, dimensions_omitted?, reviewers?, method?,
 *              gate?, cached? }
 *   canvas   the plain-English canvas line, when there is one
 *
 * An event without ``schema_version`` is v1: it renders as it always
 * has — its summary, an origin chip, no fold and no pill.
 *
 * Pure: events in → labels out. No React, no state.
 */

// The roster, in the mock's order. ``cls`` is the chip colour; ``act``
// is what the agent is doing, as the canvas says it — "IngestIQ is
// exploring your data", "IngestIQ is still planning, 4 min" — naming
// the service, never the sub-agent (sub-agent names stay in the Brain).
export const BRAIN_AGENTS = {
  mother: { name: "Mother", group: "Orchestrator", cls: "mother", act: "planning" },
  discovery: { name: "Discovery", group: "IngestIQ", cls: "ingest", act: "exploring your data" },
  planner: { name: "Planner", group: "IngestIQ", cls: "ingest", act: "planning" },
  executor: { name: "Executor", group: "IngestIQ", cls: "ingest", act: "running the work" },
  reviewer: { name: "Reviewer", group: "IngestIQ", cls: "review", act: "checking the work" },
  analyst: { name: "Analyst", group: "InsightIQ", cls: "insight", act: "calculating" },
  writer: { name: "Writer", group: "InsightIQ", cls: "insight", act: "writing the report" },
  checker: { name: "Checker", group: "InsightIQ", cls: "review", act: "checking the figures" },
};

// The service each roster group speaks for (the ``phase.service`` /
// ``origin`` vocabulary).
export const SERVICE_OF_GROUP = { Orchestrator: "orchestrator", IngestIQ: "ingestiq", InsightIQ: "insightiq" };

// A v1 ``origin`` → the service that spoke.
export const SERVICE_OF_ORIGIN = {
  mother: "orchestrator",
  memory: "orchestrator",
  ingestiq: "ingestiq",
  visioniq: "ingestiq",
  insightiq: "insightiq",
};

export const BRAIN_AGENT_GROUPS = ["Orchestrator", "IngestIQ", "InsightIQ"];

// Which agents may speak for which origin (the emitter enforces the
// same pairing; a reader that sees anything else falls back to v1).
const AGENTS_BY_ORIGIN = {
  mother: new Set(["mother"]),
  ingestiq: new Set(["discovery", "planner", "executor", "reviewer"]),
  insightiq: new Set(["analyst", "writer", "checker"]),
};

// The v1 chip: the service that spoke. ``user`` is the reader's own
// answer to a card, as lib/brainAnswers.js records it in the feed.
export const BRAIN_ORIGINS = {
  mother: { name: "Mother", cls: "mother" },
  ingestiq: { name: "IngestIQ", cls: "ingest" },
  insightiq: { name: "InsightIQ", cls: "insight" },
  visioniq: { name: "VisionIQ", cls: "ingest" },
  memory: { name: "Memory", cls: "mother" },
  user: { name: "You", cls: "you" },
};

// A phase's service → the canvas stage chip.
export const BRAIN_SERVICES = {
  orchestrator: { name: "Mother", cls: "mother" },
  ingestiq: { name: "IngestIQ", cls: "ingest" },
  insightiq: { name: "InsightIQ", cls: "insight" },
};

/**
 * The one order the feed is shown in, live and replayed: by ``ts``, then
 * by ``id``. Events built in the same millisecond share a ``ts``; the
 * emitter's ids rise in emit order within one process, and the replay
 * (``GET /task/{id}/brain``) is served in id order, so the id settles a
 * tie the same way on both paths. Works on events and on canvas lines.
 */
export function compareBrainEvents(a, b) {
  const ta = Number.isFinite(a?.ts) ? a.ts : 0;
  const tb = Number.isFinite(b?.ts) ? b.ts : 0;
  if (ta !== tb) return ta - tb;
  const ia = a?.id == null ? "" : String(a.id);
  const ib = b?.id == null ? "" : String(b.id);
  return ia < ib ? -1 : ia > ib ? 1 : 0;
}

/** True for a contract v2 event. */
export function isBrainV2(evt) {
  return !!evt && typeof evt === "object" && Number(evt.schema_version) >= 2;
}

/** The v2 sub-agent that acted, or null (v1, unknown, or mis-paired). */
export function brainAgentOf(evt) {
  if (!isBrainV2(evt)) return null;
  const agent = typeof evt.agent === "string" ? evt.agent : "";
  if (!BRAIN_AGENTS[agent]) return null;
  const allowed = AGENTS_BY_ORIGIN[evt.origin];
  if (allowed && !allowed.has(agent)) return null;
  return agent;
}

/**
 * The chip for an event: the v2 sub-agent, else the v1 service.
 *
 * @returns {{name:string, cls:string}|null}
 */
export function brainAgentLabel(evt) {
  const agent = brainAgentOf(evt);
  if (agent) return { name: BRAIN_AGENTS[agent].name, cls: BRAIN_AGENTS[agent].cls };
  const origin = BRAIN_ORIGINS[evt?.origin];
  return origin ? { name: origin.name, cls: origin.cls } : null;
}

/** The v2 phase, when it is well formed. */
export function brainPhaseOf(evt) {
  if (!isBrainV2(evt)) return null;
  const p = evt.phase;
  if (!p || typeof p !== "object") return null;
  const id = typeof p.id === "string" ? p.id.trim() : "";
  if (!id) return null;
  const name = typeof p.name === "string" && p.name.trim() ? p.name.trim() : id;
  return { id, name, service: BRAIN_SERVICES[p.service] ? p.service : null };
}

// ── verdicts ────────────────────────────────────────────────

const VERDICT_PILL = {
  approved: { label: "Approved", cls: "ok" },
  rejected: { label: "Rejected", cls: "no" },
  unavailable: { label: "Review could not be completed", cls: "na" },
};

/** The verdict, when an event carries a recognised one. */
export function brainVerdictOf(evt) {
  if (!isBrainV2(evt)) return null;
  const v = evt.verdict;
  if (!v || typeof v !== "object" || !VERDICT_PILL[v.outcome]) return null;
  return v;
}

/** ``{label, cls}`` for the verdict pill. */
export function verdictPill(outcome) {
  return VERDICT_PILL[outcome] || null;
}

// Reviewer dimension names → the words a reader uses. The emitter
// sends the engine's names; the mapping lives here, in one place.
export const DIMENSION_LABELS = {
  numeric_representation: "numbers",
  zero_null: "zeros and blanks",
  memory_precedence: "your saved rules",
  formula: "formula",
  population: "population",
  grouping: "grouping",
  dates: "dates",
  exact_rows: "row count",
  min_rows: "minimum rows",
  max_rows: "maximum rows",
  required_columns: "required columns",
  read_only: "read-only",
  duplicate_keys: "duplicate keys",
  null_keys: "blanks in keys",
};

/** A dimension name as a reader would say it. */
export function dimensionLabel(name) {
  const key = String(name || "").trim();
  if (!key) return "";
  if (DIMENSION_LABELS[key]) return DIMENSION_LABELS[key];
  return key.replace(/[_-]+/g, " ").toLowerCase();
}

export const DIMENSION_STATUS_LABELS = {
  consistent: "consistent",
  conflict: "conflict",
  unresolved: "unresolved",
  not_applicable: "not applicable",
  passed: "passed",
  failed: "failed",
};

/** True for a status the reader should notice (amber). */
export function isDimensionProblem(status) {
  return status === "conflict" || status === "failed" || status === "unresolved";
}

// ── code and notes ──────────────────────────────────────────

const CODE_LANG_LABEL = { sql: "SQL", python: "Python" };

const DIALECT_LABEL = {
  duckdb: "DuckDB",
  postgresql: "PostgreSQL",
  mysql: "MySQL",
  mssql: "SQL Server",
  snowflake: "Snowflake",
  redshift: "Redshift",
  athena: "Athena",
};

/** The code block, when it has text in a known language. */
export function brainCodeOf(evt) {
  if (!isBrainV2(evt)) return null;
  const c = evt.code;
  if (!c || typeof c !== "object" || !CODE_LANG_LABEL[c.lang]) return null;
  if (typeof c.text !== "string" || !c.text) return null;
  return c;
}

/** ``SQL`` / ``Python``. */
export function codeLangLabel(lang) {
  return CODE_LANG_LABEL[lang] || "Code";
}

/** ``DuckDB``, ``PostgreSQL``, … or the dialect as sent. */
export function dialectLabel(dialect) {
  if (typeof dialect !== "string" || !dialect) return "";
  return DIALECT_LABEL[dialect] || dialect;
}

/** The plain-text Details body, when there is one. */
export function brainNoteOf(evt) {
  if (!isBrainV2(evt)) return null;
  return typeof evt.note === "string" && evt.note.trim() ? evt.note : null;
}

// ── metrics ─────────────────────────────────────────────────

/** ``+12s`` / ``+1m 05s`` from the start of the task. */
export function formatBrainOffset(ms) {
  const s = Math.max(0, Math.floor((Number(ms) || 0) / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return m ? `+${m}m ${String(r).padStart(2, "0")}s` : `+${r}s`;
}

const nf = (n) => Number(n).toLocaleString("en-GB");
const isCount = (n) => Number.isInteger(n) && n >= 0;
const plural = (n, one, many) => `${nf(n)} ${n === 1 ? one : many}`;

/** ``12 ms`` / ``0.4 s`` / ``6.2 s`` / ``38 s`` / ``1m 58s``. */
export function formatBrainMs(ms) {
  if (ms < 100) return `${ms} ms`;
  const s = ms / 1000;
  if (s < 9.95) return `${s.toFixed(1)} s`;
  const whole = Math.round(s);
  if (whole < 60) return `${whole} s`;
  return `${Math.floor(whole / 60)}m ${whole % 60}s`;
}

function formatBytesShort(bytes) {
  if (bytes < 1024) return `${nf(bytes)} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/**
 * The metrics line of a v2 event, in the mock's order: counts, then
 * time, then rows — ``1 call · 6.2 s``, ``38 s · 853,520 rows``,
 * ``2 reviewers · 4.0 s``, ``0 calls · deterministic``. A reviewer
 * count of 0 (the read-only gate) is never shown as a reviewer.
 *
 * @returns {string[]} the parts, joined by `` · `` when rendered
 */
export function brainMetricParts(evt) {
  const m = evt && typeof evt.metrics === "object" && evt.metrics ? evt.metrics : {};
  const v = brainVerdictOf(evt);
  const parts = [];
  if (isCount(m.calls)) parts.push(plural(m.calls, "call", "calls"));
  if (isCount(m.queries)) parts.push(plural(m.queries, "query", "queries"));
  const reviewers = isCount(m.reviewers) ? m.reviewers : v?.reviewers;
  if (isCount(reviewers) && reviewers > 0)
    parts.push(plural(reviewers, "reviewer", "reviewers"));
  if (isCount(m.notes)) parts.push(plural(m.notes, "note", "notes"));
  if (isCount(m.ms)) parts.push(formatBrainMs(m.ms));
  if (isCount(m.rows)) parts.push(plural(m.rows, "row", "rows"));
  if (isCount(m.tokens)) parts.push(plural(m.tokens, "token", "tokens"));
  if (isCount(m.bytes)) parts.push(formatBytesShort(m.bytes));
  if (v?.method === "deterministic") parts.push("deterministic");
  if (v?.cached === true) parts.push("reused");
  return parts;
}
